import React, { useContext, useRef } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import SvgHeadingSwapIcon from '@app/icons/heading/HeadingSwapIcon.tsx';
import RingilDataTable, {
  ColumnConfig,
  FilterConfig,
  FilterType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import { stringDateTimeWithLocaleFromApiStringInTz, dayjsNowInTz } from '@utils/dateWithTz.tsx';
import { ApiContext } from '@api/api.tsx';
import {
  ArrivalsDeparturesLogResponse,
  EntityTypeEnum,
  FeaturesEnum,
  ListArrivalsDeparturesLogFilter,
  Pageable,
  PermissionTypesEnum,
} from '@api/ringil-api.v2.tsx';
import CamsTabs from '@app/pages/cams/tabs/CamsTabs.tsx';
import COText from '@app/pages/COText/COText.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { useCreateTimeslotSidebar } from '@components/ringil3/Features/Reservation/Sidedbars/CreateTimeslotSidebar.tsx';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import SvgCalendar from '@components/framework/icons/Calendar.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import LinkedResBadge from '@components/ringil3/Badges/LinkedResBadge.tsx';
import { useAdminAddCarPlateSidebar } from '@app/pages/cams/seidebars/AdminAddCarPlateSidebar.tsx';
import { useCreateAddCarPlateSidebar } from '@app/pages/cams/seidebars/AddCarPlateSidebar.tsx';
import TransportationTypeTruck from '@components/framework/icons/TransportationTypes/TransportationTypeTruck.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';

interface ComponentProps {
}

const CamsDetail: React.FC<ComponentProps> = () => {
  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const { listArrivalsDeparturesLog } = useContext(ApiContext);
  const { loggedUser, hasPermission } = useContext(AuthenticatedUserContext);
  const hasAdminPermission = hasPermission([PermissionTypesEnum.PERMISSION_ADMIN]);
  const hasCamSettingsPermission = hasPermission([PermissionTypesEnum.PERMISSION_CAM_SETTINGS]);
  const companyTimezone = loggedUser?.defaultTimezoneId ?? 'UTC';

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  const camLogExportFlag = useSecuredFlag('cam-log-export', {
    kind: 'action',
    description: 'Export camera entry log (generic export API)',
    features: [FeaturesEnum.GENERIC_EXPORT_API],
    permissions: [PermissionTypesEnum.PERMISSION_CAM_LOG_EXPORT],
  });

  const { CreateTimeslotSidebar, setCreateTimeslotRequest, createTimeslotRequest } = useCreateTimeslotSidebar();
  const {
    AdminAddCarPlateSidebar,
    setAdminAddCarPlateSidebarVisible,
  } = useAdminAddCarPlateSidebar();

  const {
    AddCarPlateSidebar,
    openSidebar: openAddCarPlateSidebar,
  } = useCreateAddCarPlateSidebar();

  const reloadTable = () => tableRef.current?.reloadData();

  const loadDataFromApi = (filter: ListArrivalsDeparturesLogFilter, pageable: Pageable, onSuccess: (data: PagedData<ArrivalsDeparturesLogResponse>) => void) => {
    listArrivalsDeparturesLog(filter, pageable, d => onSuccess(d));
  };

  // The Allowed/Denied filter is held as a UI string ('ALLOWED' | 'DENIED' | 'ALL') so the dropdown can
  // round-trip it; convert it to the boolean the API expects (and that the export job receives) here.
  const normalizeFilter = (filter: ListArrivalsDeparturesLogFilter): ListArrivalsDeparturesLogFilter => {
    const raw = (filter as { isAllowed?: unknown })?.isAllowed;
    return {
      ...filter,
      isAllowed: raw === 'ALLOWED' ? true : raw === 'DENIED' ? false : undefined,
    };
  };

  const openCreateReservationSidebar = (cameraId: string, carPlate: string, companyLocationId: string, cameraLogId: string, timezoneId: string) => {
    setCreateTimeslotRequest({
      companyLocationId,
      preferredDate: dayjsNowInTz(timezoneId ?? loggedUser?.defaultTimezoneId ?? 'Europe/Prague').format('YYYY-MM-DD'),
      cameraId,
      carPlate,
      companyLocationCustomerId: loggedUser?.companyId,
      applicationId: null,
      expeditionId: null,
      cameraLogId,
      timezoneId: timezoneId,
    });
  };

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr('CamsDetail.spz', 'SPZ'),
      value: ['carPlate'],
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('CamsDetail.kamera', 'Kamera'),
      value: ['cameraName'],
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('CamsDetail.osoba', 'Osoba'),
      value: ['personName'],
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('CamsDetail.selectAll', 'All'),
      value: ['isAllowed'],
      options: [
        { label: tr('CamsDetail.selectAllowed', 'Allowed'), value: 'ALLOWED', active: true },
        { label: tr('CamsDetail.selectDenied', 'Denied'), value: 'DENIED', active: true },
        { label: tr('CamsDetail.selectAll', 'All'), value: 'ALL', active: true },
      ],
    },
  ];

  const columnConfig: ColumnConfig<ArrivalsDeparturesLogResponse>[] = [
    {
      id: 'spz',
      header: 'CamsDetail.spz',
      headerDefault: 'SPZ',
      order: 1,
      render: row => (
        <div className={'flex flex-col gap-2'}>
          <div>{row.carPlate}</div>
          <div>{row.personName}</div>
        </div>
      ),
    },
    {
      id: 'arrival',
      header: 'CamsDetail.prijezd',
      headerDefault: 'Příjezd',
      order: 2,
      render: row => (
        <div className={'flex flex-col gap-2'}>
          {row?.isArrival == true && (
            <>
              <div>{stringDateTimeWithLocaleFromApiStringInTz(row.createdAt, companyTimezone)}</div>
              <div>{row.cameraName}</div>
            </>
          )}
        </div>
      ),
    },
    {
      id: 'departure',
      header: 'CamsDetail.odjezd',
      headerDefault: 'Odjezd',
      order: 3,
      render: row => (
        <div className={'flex flex-col gap-2'}>
          {row?.isArrival == false && (
            <>
              <div>{stringDateTimeWithLocaleFromApiStringInTz(row.createdAt, companyTimezone)}</div>
              <div>{row.cameraName}</div>
            </>
          )}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'CamsDetail.status',
      headerDefault: 'Status',
      order: 4,
      render: row => (
        <div className={'flex flex-col gap-2'}>
          {row?.isAllowed === true ? (
            <COText label={tr('CamsDetail.povoleno', 'Povoleno')} />
          ) : (
            <div className={'flex flex-row gap-2 items-center'}>
              <COText label={tr('CamsDetail.zamitnuto', 'Zamítnuto')} />
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      headerDefault: '',
      order: 5,
      render: row => (
        <div className={'text-right'}>
          {row?.isAllowed === false && (
            <div className={'flex flex-row gap-2 justify-end items-center'}>
              <LinkedResBadge linkedReservation={row.reservationLink} />
              {hasCamSettingsPermission && (
                <RingilButton
                  mode={'table'}
                  icon={<TransportationTypeTruck />}
                  onClick={() => openAddCarPlateSidebar(row.carPlate)}
                />
              )}
              <RingilButton
                mode={'table'}
                icon={<SvgCalendar />}
                onClick={() => openCreateReservationSidebar(row.cameraId, row.carPlate, row.cameraCompanyLocationId, row.id, row.timezoneId)}
              />
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={'flex flex-col gap-2'}>
      <BreadCrumbComponent
        items={[
          {
            label: tr(`ReservationDetail.bcReservationList`, `Seznam rezervací`),
            url: nav.createNavigationLink(nav.urlFunctions.createReservationList()),
          },
          { label: tr('CamsDetail.kamery', 'Kamery') },
        ]}
      />
      <div className="flex justify-between items-center">
        <COHeadline variant={'h2'} title={tr('CamsDetail.kamery', 'Kamery')} />
        {hasAdminPermission && (
          <RingilButton
            mode={'filled-rectangle'}
            label={tr('CamsDetail.addCarPlate', 'Add Car Plate')}
            onClick={() => setAdminAddCarPlateSidebarVisible(true)}
          />
        )}
      </div>
      <CamsTabs selectedPanel={'log'} />

      <div className={'my-3 flex flex-row items-center gap-2'}>
        <COHeadline variant={'h2'} title={tr('CamsDetail.pohybyVozidelVArealu', 'Pohyby vozidel v areálu')}
                    icon={<SvgHeadingSwapIcon />}></COHeadline>
        <COTimezone timezoneId={companyTimezone} />
      </div>

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnConfig as ColumnConfig<unknown>[]}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataFromApi}
        normalizeFilter={normalizeFilter}
        filterName={'cams_detail'}
        enableReload={true}
        exportConfig={{
          entityType: EntityTypeEnum.CAMERA_LOG,
          entityLabel: tr('CamsDetail.exportEntityLabel', 'Camera log'),
          secured: camLogExportFlag,
        }}
      />

      {isNotNullOrUndefined(createTimeslotRequest) && (
        <CreateTimeslotSidebar
          onContinue={() => reloadTable()}
          headerDisabled={false}
          showCamera={true}
        />
      )}

      <AdminAddCarPlateSidebar
        onSaved={() => {
          setAdminAddCarPlateSidebarVisible(false);
          reloadTable();
        }}
      />

      <AddCarPlateSidebar
        onSaved={() => {
          reloadTable();
        }}
      />

    </div>
  );
};

export default CamsDetail;